/**
 * ``useSaveProfile`` — the shared "snapshot the live config into a Profile"
 * machinery (ADR-033 §5; REQ-UI-076 / 078).
 *
 * # Why a shared hook
 *
 * Two surfaces save the **current working state** as a new Profile, and both
 * must capture the *same* live extras — the panel layout and the map viewport —
 * not just the scalar working-state slices:
 *
 *   - the **Settings panel**'s "Save as new Profile" (ADR-033 §5.2), and
 *   - the **gear menu**'s "New Profile…" name prompt (Flow U; part 2).
 *
 * A third surface — **Import Profile…** (Flow U; ADR-033 §11.2) — creates a new
 * Profile from an *explicit imported document* rather than the live working
 * state. :func:`saveProfileFromDocument` is the shared create → apply → refresh
 * primitive both paths use; ``saveProfileAsNew`` is just it applied to the live
 * config captured at call time.
 *
 * Before part 2 the build-document + create + apply-by-value logic lived inside
 * the Settings drawer. Extracting it here keeps the two call sites byte-for-byte
 * consistent: the saved document is the working state with the **live dockview
 * layout** (:func:`MultiPaneApi.captureLayout`) and the **live map camera**
 * (``map-viewport-store`` → ``map.home``) folded in at save time — so re-applying
 * the Profile restores the operator's exact panel arrangement and flies back to
 * their viewport.
 *
 * # Update-current-Profile (architect 2026-06-08, correctness fix)
 *
 * :func:`UseSaveProfile.updateProfileFromLive` is the shared back-end for BOTH
 * "Update current Profile" entry points — the Settings-panel footer button AND
 * the gear-menu Profile row's pencil. Both now build + diff the **full** live
 * document (working_state + live layout + live camera), so a pan/zoom or panel
 * rearrange counts as a difference and is persisted; "Nothing to update" shows
 * only when the full document truly matches. Previously the gear-menu pencil
 * diffed (and persisted) ``working_state`` alone — a layout/viewport change read
 * as a false no-op. The reactive :func:`useLiveSnapshotDocument` derives the
 * same full document so the top-bar "(modified)" indicator agrees with the
 * update diff (REQ-UI-074).
 *
 * # What it does NOT do
 *
 * - **No name/scope UI.** The caller owns the prompt (the drawer's inline form;
 *   the menu's name prompt) and hands a validated ``{ name, scope }`` in.
 * - **No clock-dup validation.** That belongs to the Settings panel's form
 *   (which surfaces it inline per-row); a caller without that affordance should
 *   pre-check via its own means. The BFF strict-validates regardless.
 *
 * On success the new Profile becomes the **active** Profile: the hook re-applies
 * the saved document by value (so ``active_profile`` + the "(modified)" baseline
 * hash track it). Errors are mapped to generic, user-facing copy (REQ-UI-037) —
 * the caller surfaces them inline.
 *
 * Cross-references:
 *   - REQ-UI-076 (Settings panel — live edit + save).
 *   - REQ-UI-078 (save scope; Shared admin-gated — the gate is the caller's).
 *   - REQ-UI-074 / 075 (apply copies by value; active-Profile + "(modified)").
 *   - REQ-UI-037 (generic user-facing error copy).
 *   - ADR-033 §5 / §4.2; ADR-024 §6 (the layout capture).
 *   - profiles/profile-editor.tsx + preferences/preferences-menu.tsx (callers).
 */
import { useCallback, useMemo } from "react";
import type { ActiveProfileRef, ProfileDocument, ProfileSummary } from "@/auth/session-types";
import {
  foldStatusSectionOverrides,
  getStatusSectionOverrides,
} from "@/entity-details/use-section-visibility";
import { ApiError } from "@/lib/api-client";
import { useMapViewport, useMapViewportStore } from "@/map/map-viewport-store";
import { useMultiPaneApi } from "@/multi-pane/multi-pane-context";
import { usePreferences } from "@/preferences/preferences-context";
import {
  createProfile,
  updateProfile,
  type ProfileCreateRequest,
} from "@/profiles/profiles-client";
import { sha256CanonicalJson } from "@/profiles/profile-hash";
import { useProfiles } from "@/profiles/profiles-context";

/** A Profile scope on the wire (``"global"`` displays as "Shared" in the UI;
 *  ADR-033 §4.1 / REQ-UI-078 — Shared creation is admin-gated). */
export type ProfileScope = "global" | "user";

/** The discriminated result of a save. ``status === "error"`` carries generic,
 *  user-facing copy (REQ-UI-037) the caller renders inline without throwing. */
export type SaveProfileResult = { status: "ok" } | { status: "error"; message: string };

/**
 * The discriminated result of an **update-current-Profile** (REQ-UI-076).
 * Distinct from {@link SaveProfileResult} so the caller can route the
 * full-snapshot-diff outcomes to the right UX (ADR-033 §5):
 *   - ``"ok"`` — the full live document differed and was persisted; the active
 *     pointer/"(modified)" baseline was re-based when the edited Profile is the
 *     active one.
 *   - ``"unchanged"`` — the full live document (working_state **+ live layout +
 *     live map camera**) already matches the Profile's stored document; no PUT.
 *     The caller surfaces "Nothing to update".
 *   - ``"cancelled"`` — the optional ``confirm`` hook returned ``false`` (the
 *     gear menu's ``window.confirm``); nothing was written.
 *   - ``"error"`` — the fetch or PUT failed; ``message`` is generic copy.
 */
export type UpdateProfileResult =
  | { status: "ok" }
  | { status: "unchanged" }
  | { status: "cancelled" }
  | { status: "error"; message: string };

/** Decimal places the persisted / compared map ``home`` is quantized to so a
 *  flyTo's float-noise settle is not a false "(modified)" change (REQ-UI-074):
 *  6 dp of lon/lat ≈ 0.11 m; 4 dp of zoom is far finer than any meaningful
 *  operator zoom. Applied in {@link buildLiveDocument} — the single place BOTH
 *  the saved document and the live dirty-check fold the camera in — so they
 *  agree across the save → re-fly → settle round trip. */
const HOME_COORD_DP = 6;
const HOME_ZOOM_DP = 4;

/** Round ``value`` to ``dp`` decimals. Deterministic: two cameras within the
 *  rounding granularity map to the byte-identical IEEE-754 result, so a
 *  quantized ``home`` hashes identically before/after a flyTo settle. */
function roundTo(value: number, dp: number): number {
  const factor = 10 ** dp;
  return Math.round(value * factor) / factor;
}

/**
 * Build the full document a save / update would persist: ``workingState`` with
 * the **live** dockview layout and the **live** map camera (written into
 * ``map.home``) folded in. The single source of truth for "what the live
 * configuration is right now" — shared by the imperative
 * :func:`UseSaveProfile.buildSaveDocument` (capture at click time) and the
 * reactive :func:`useLiveSnapshotDocument` (the "(modified)" indicator), so the
 * Update-current diff and the indicator can never disagree (REQ-UI-074).
 *
 * Layout is omitted when no workspace is mounted (``layout === null``); ``home``
 * is omitted when the map hasn't published a camera yet (``viewport === null``)
 * — matching the create/update bodies' optionality (ADR-033 §5 / §9.4).
 *
 * The map ``home`` is **quantized** ({@link HOME_COORD_DP} / {@link HOME_ZOOM_DP})
 * before it is folded in: after a Profile apply / Update-current writes ``home``
 * ← the live viewport, the Map re-flies there and MapLibre settles at a camera
 * that differs by float noise (~1e-9°). The settled value is republished and
 * would otherwise make ``map.home`` mismatch the stored baseline forever — a
 * persistent false "(modified)" (REQ-UI-074). Quantizing absorbs that drift
 * while preserving any real pan/zoom (a 0.11 m / 1e-4-zoom move still counts).
 *
 * @param workingState - The live working-state document (scalar slices).
 * @param layout - The captured live layout, or ``null`` (no workspace / not
 *     ready).
 * @param viewport - The live map camera, or ``null`` (map not loaded).
 */
export function buildLiveDocument(
  workingState: ProfileDocument,
  layout: ProfileDocument["layout"] | null,
  viewport: { center: { lon: number; lat: number }; zoom: number } | null,
  statusSectionOverrides: ReadonlyMap<string, boolean> | null = null,
): ProfileDocument {
  const doc: ProfileDocument = { ...workingState };
  if (statusSectionOverrides !== null && statusSectionOverrides.size > 0) {
    // REQ-UI-131 — the System panel's section collapse is session-local and
    // never written on toggle; **saving** is what captures it. Fold the live
    // session states in here, alongside the other two live extras the scalar
    // working state never sees. A session that touched no status section folds
    // nothing, so the document is byte-identical to the working state.
    doc.entity_details = foldStatusSectionOverrides(doc.entity_details, statusSectionOverrides);
  }
  if (layout !== null) {
    // Fold the LIVE layout in, but PRESERVE the fields the persisted
    // ``workingState.layout`` carries that the live ``captureLayout()`` omits —
    // notably the vestigial ``preset_name``. The BFF's ``LayoutSettings`` keeps
    // ``preset_name`` and serialises it (as ``null``) in every response, but the
    // live capture no longer emits it. Without this merge the live snapshot's
    // layout lacks ``preset_name`` while the stored baseline carries
    // ``preset_name: null``, so the canonical hashes never match and the active
    // Profile reads as permanently "(modified)" even right after saving
    // (REQ-UI-074). The live ``layout`` still wins for the fields it does emit
    // (panels / dockview_state / version), so a real layout change flips the
    // marker; robust to any future round-trip-only field too.
    doc.layout = workingState.layout === undefined ? layout : { ...workingState.layout, ...layout };
  }
  if (viewport !== null) {
    doc.map = {
      ...doc.map,
      home: {
        center: {
          lon: roundTo(viewport.center.lon, HOME_COORD_DP),
          lat: roundTo(viewport.center.lat, HOME_COORD_DP),
        },
        zoom: roundTo(viewport.zoom, HOME_ZOOM_DP),
      },
    };
  }
  return doc;
}

/** The hook's surface: a document builder (shared with the editor's
 *  update-active path) + the save-as-new + save-from-document flows. */
export interface UseSaveProfile {
  /**
   * Build the document a save would persist: the current working state with the
   * **live** dockview layout (:func:`MultiPaneApi.captureLayout`) and the
   * **live** map camera (written into ``map.home``) folded in. Capture happens
   * at call time, not continuously — an in-flight tweak never persists layout /
   * viewport churn into the working state.
   */
  buildSaveDocument: () => ProfileDocument;
  /**
   * Snapshot the current working state (+ live layout + live viewport) into a
   * **new** Profile via ``createProfile``, then re-apply it by value so it
   * becomes the active Profile. The caller supplies a validated, trimmed name
   * and a scope (Shared admin-gating is the caller's responsibility — the BFF is
   * the primary control per REQ-AUTH-074). Refreshes the Profiles list on
   * success. Returns a discriminated result (never throws).
   */
  saveProfileAsNew: (name: string, scope: ProfileScope) => Promise<SaveProfileResult>;
  /**
   * Create a new Profile from an **explicit** document (NOT the live working
   * state) and re-apply it by value so it becomes the active Profile. This is
   * the import path (ADR-033 §11.2): the document comes from a validated export
   * wrapper, so the new Profile carries the *imported* configuration — the live
   * config is untouched until the apply lands. Same create → refresh → apply →
   * error-mapping contract as :func:`saveProfileAsNew` (which delegates here);
   * the 409 name-collision surfaces as a distinct message for inline rename.
   */
  saveProfileFromDocument: (
    name: string,
    scope: ProfileScope,
    document: ProfileDocument,
  ) => Promise<SaveProfileResult>;
  /**
   * **Update an existing Profile to the current live configuration** — the
   * shared back-end for BOTH update entry points (the Settings-panel footer
   * "Update current Profile" button AND the gear-menu Profile row's pencil), so
   * they capture and diff the live state identically (ADR-033 §5; REQ-UI-076).
   *
   * Builds the FULL live document (:func:`buildSaveDocument` — working_state +
   * live layout + live map camera), fetches the Profile's stored document, and
   * compares canonical hashes:
   *   - identical ⇒ returns ``{ status: "unchanged" }`` (no PUT). A layout-only
   *     or viewport-only change counts as a difference, because both are folded
   *     into the compared document — fixing the false "Nothing to update" after
   *     a pan/zoom or panel rearrange.
   *   - different ⇒ (optionally gate on ``confirm``) ``updateProfile`` with the
   *     full document, refresh the list, and — when the edited Profile is the
   *     **active** one — re-apply by value so the active pointer +
   *     "(modified)" baseline reflect the just-saved document.
   *
   * The fetch is by-id so a stale list never persists an out-of-date diff. The
   * confirmation prompt is the caller's (the gear menu confirms a destructive
   * overwrite via ``window.confirm``; the Settings panel does not) — passed as
   * ``confirm`` and invoked **only** when there is a real difference, so the
   * operator is never prompted for a no-op.
   *
   * @param summary - The Profile to update (id + name + scope from the list).
   * @param opts.confirm - Optional gate invoked once a difference is found;
   *     returning ``false`` aborts with ``{ status: "cancelled" }``.
   */
  updateProfileFromLive: (
    summary: Pick<ProfileSummary, "profile_id" | "name">,
    opts?: { confirm?: () => boolean },
  ) => Promise<UpdateProfileResult>;
}

/**
 * The live configuration document — ``workingState`` with the **live** dockview
 * layout + the **live** map camera folded in — recomputed reactively (REQ-UI-074;
 * ADR-033 §5). The reactive twin of :func:`UseSaveProfile.buildSaveDocument`:
 * the builder captures at click time for a save, this hook re-derives on every
 * change so the "(modified)" indicator reflects layout / viewport churn the
 * scalar working state never sees.
 *
 * Reactivity sources:
 *   - ``workingState`` (a Preferences re-render on any scalar edit);
 *   - the live map camera via :func:`useMapViewport` (a Zustand selector that
 *     re-renders on every settled pan/zoom);
 *   - the multi-pane :attr:`MultiPaneApi.layoutRevision` nonce (a re-render on
 *     every dockview drag / dock / resize / close), which gates a fresh
 *     :func:`MultiPaneApi.captureLayout` read.
 *
 * Because ``captureLayout`` walks dockview's grid, it is read only when
 * ``layoutRevision`` (or the API identity) changes — not on every unrelated
 * re-render — keeping the recompute cheap (ADR-024 §6).
 *
 * **One deliberate asymmetry with the save builder (REQ-UI-131).** The status
 * panel's session-local section collapse is folded in by
 * :func:`UseSaveProfile.buildSaveDocument` but **not** here: collapsing
 * "Resource utilization" while triaging an incident must not light the top bar's
 * "(modified)" marker, because it is not a settings change the operator made —
 * it is a transient disclosure. The asymmetry can only ever cause an Update-
 * current to persist while the marker reads clean (the states get captured,
 * which is the requirement); it can never cause the false "Nothing to update"
 * this hook exists to prevent, since the save side folds in strictly *more*.
 *
 * @returns The full live document a save / update would persist right now.
 */
export function useLiveSnapshotDocument(workingState: ProfileDocument): ProfileDocument {
  const multiPaneApi = useMultiPaneApi();
  const viewport = useMapViewport();
  // Read the live layout, re-derived when the layout actually moves
  // (``layoutRevision``) or the workspace (un)mounts (API identity) — NOT on
  // every render, since ``captureLayout`` walks the dockview grid.
  const layoutRevision = multiPaneApi?.layoutRevision ?? null;
  const layout = useMemo(
    () => multiPaneApi?.captureLayout() ?? null,
    // ``layoutRevision`` is the change signal; ``multiPaneApi`` covers
    // mount/unmount. ``captureLayout`` is stable per API identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [multiPaneApi, layoutRevision],
  );
  return useMemo(
    () => buildLiveDocument(workingState, layout, viewport),
    [workingState, layout, viewport],
  );
}

/**
 * Map a save failure to user-facing copy (REQ-UI-037 — never leak BFF
 * internals). The 409 name-collision copy is distinct from the 422
 * document-rejection copy so the inline error tells the operator which lever to
 * pull (rename vs. check the values); 403 means the BFF rejected the scope.
 */
export function describeSaveError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return "A profile with that name already exists in this scope. Choose a different name.";
    }
    if (error.status === 403) {
      return "You don't have permission to save this profile.";
    }
    if (error.status === 422) {
      return "We couldn't save this profile — please check the values and try again.";
    }
  }
  return "Couldn't save this profile — please try again.";
}

/**
 * Map an **update-current-Profile** failure to user-facing copy (REQ-UI-037).
 * Mirrors :func:`describeSaveError` but with update-phrased copy so the inline
 * error matches the action the operator took: 409 = name collision (rename),
 * 403 = scope/permission (a non-admin Shared write the BFF rejected), else a
 * generic retry. Kept separate so the two flows can diverge later.
 */
export function describeUpdateError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 409) {
      return "A profile with that name already exists in this scope.";
    }
    if (error.status === 403) {
      return "You don't have permission to update this profile.";
    }
  }
  return "Couldn't update this profile — please try again.";
}

/**
 * The shared save-as-new hook. Reads the live working state + the multi-pane +
 * map-viewport stores; returns the document builder + the save flow. Safe to
 * call from any surface under the providers (the Settings drawer and the gear
 * menu both mount beneath them).
 */
export function useSaveProfile(): UseSaveProfile {
  const { workingState, activeProfile, applyWorkingState } = usePreferences();
  const { refresh, getDocument } = useProfiles();
  const multiPaneApi = useMultiPaneApi();

  const buildSaveDocument = useCallback((): ProfileDocument => {
    // Capture the live layout + camera at CALL time (a save click), not
    // continuously — delegating the assembly to :func:`buildLiveDocument` so the
    // create / update bodies and the "(modified)" indicator fold them in
    // identically (ADR-033 §5; REQ-UI-074).
    const layout = multiPaneApi?.captureLayout() ?? null;
    const viewport = useMapViewportStore.getState().viewport;
    // The System panel's session-local section collapse (REQ-UI-131) is read
    // ONLY here, on the explicit save/update capture — see
    // :func:`useLiveSnapshotDocument` for why the reactive twin deliberately
    // leaves it out.
    return buildLiveDocument(workingState, layout, viewport, getStatusSectionOverrides());
  }, [workingState, multiPaneApi]);

  const saveProfileFromDocument = useCallback(
    async (
      name: string,
      scope: ProfileScope,
      document: ProfileDocument,
    ): Promise<SaveProfileResult> => {
      const trimmed = name.trim();
      if (trimmed.length === 0) {
        return { status: "error", message: "Name is required." };
      }
      try {
        const detail = await createProfile({
          scope,
          name: trimmed,
          document,
        } satisfies ProfileCreateRequest);
        // Refresh the list so the new Profile appears; tolerate a transient
        // refresh blip (the menu/drawer self-corrects on the next open).
        void refresh().catch(() => undefined);
        // The saved Profile becomes active — re-apply by value over the saved
        // document so the active pointer + "(modified)" baseline reflect it.
        const ref: ActiveProfileRef = {
          scope: detail.scope,
          profile_id: detail.profile_id,
          name: detail.name,
          applied_at: new Date().toISOString(),
          applied_document_sha256: await sha256CanonicalJson(detail.document),
        };
        const result = await applyWorkingState(detail.document, ref);
        if (result.status === "error") {
          return { status: "error", message: result.message };
        }
        return { status: "ok" };
      } catch (caught) {
        return { status: "error", message: describeSaveError(caught) };
      }
    },
    [refresh, applyWorkingState],
  );

  const saveProfileAsNew = useCallback(
    (name: string, scope: ProfileScope): Promise<SaveProfileResult> =>
      // Save-as-new IS save-from-document with the LIVE config captured at call
      // time (layout + viewport folded in). Delegating keeps the create → apply
      // → refresh → error-mapping in one place (shared with import).
      saveProfileFromDocument(name, scope, buildSaveDocument()),
    [saveProfileFromDocument, buildSaveDocument],
  );

  const updateProfileFromLive = useCallback(
    async (
      summary: Pick<ProfileSummary, "profile_id" | "name">,
      opts?: { confirm?: () => boolean },
    ): Promise<UpdateProfileResult> => {
      // Build the FULL live document (working_state + live layout + live camera)
      // — the SAME document Save-as-new builds — so a layout-only or
      // viewport-only change is a real difference, not a false no-op.
      const document = buildSaveDocument();
      // Fetch the Profile's stored document by id (a stale list must never seed
      // the diff) and compare canonical hashes.
      let savedHash: string;
      try {
        const detail = await getDocument(summary.profile_id);
        savedHash = await sha256CanonicalJson(detail.document);
      } catch {
        // 404 — the Profile vanished / access lost between list and update.
        return { status: "error", message: "That profile is no longer available." };
      }
      const liveHash = await sha256CanonicalJson(document);
      if (savedHash === liveHash) {
        // Full documents match — nothing to persist (REQ-UI-074 — consistent
        // with the "(modified)" indicator, which hashes the same full snapshot).
        return { status: "unchanged" };
      }
      // A real difference: gate on the caller's confirm (the gear menu's
      // destructive-overwrite prompt) — invoked ONLY now, so a no-op never
      // prompts. The Settings panel passes no confirm and proceeds.
      if (opts?.confirm !== undefined && !opts.confirm()) {
        return { status: "cancelled" };
      }
      try {
        const detail = await updateProfile(summary.profile_id, {
          name: summary.name,
          document,
        });
        // Refresh the list (tolerate a transient blip — the surface self-
        // corrects on the next open).
        void refresh().catch(() => undefined);
        // When the edited Profile is the ACTIVE one, re-apply by value so the
        // active pointer + "(modified)" baseline track the just-saved document
        // (the working state now matches it). Editing a non-active Profile
        // leaves the active pointer untouched.
        if (activeProfile?.profile_id === summary.profile_id) {
          const ref: ActiveProfileRef = {
            scope: detail.scope,
            profile_id: detail.profile_id,
            name: detail.name,
            applied_at: new Date().toISOString(),
            applied_document_sha256: await sha256CanonicalJson(detail.document),
          };
          const result = await applyWorkingState(detail.document, ref);
          if (result.status === "error") {
            return { status: "error", message: result.message };
          }
        }
        return { status: "ok" };
      } catch (caught) {
        return { status: "error", message: describeUpdateError(caught) };
      }
    },
    [buildSaveDocument, getDocument, refresh, activeProfile, applyWorkingState],
  );

  return {
    buildSaveDocument,
    saveProfileAsNew,
    saveProfileFromDocument,
    updateProfileFromLive,
  };
}
